interface ToggleProps {
  checked: boolean;
  /** Describes what is being switched, for assistive technology. */
  label: string;
  /**
   * Omit to render a read-only indicator rather than a control.
   *
   * A switch that looks interactive and does nothing invites a click and answers with silence;
   * without a handler this is an image of a state, and is not focusable.
   */
  onChange?: (next: boolean) => void;
}

/** The app's on/off switch. */
export function Toggle({ checked, label, onChange }: ToggleProps) {
  const track = `relative h-6 w-11 shrink-0 rounded-full transition-colors ${
    checked ? "bg-accent" : "bg-neutral-200"
  }`;
  const thumb = `absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform ${
    checked ? "translate-x-5" : ""
  }`;

  if (onChange === undefined) {
    return (
      <span className={track} role="img" aria-label={`${label}: ${checked ? "on" : "off"}`}>
        <span className={thumb} />
      </span>
    );
  }

  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => {
        onChange(!checked);
      }}
      className={`${track} focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent`}
    >
      <span className={thumb} />
    </button>
  );
}
